import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import { apiClient } from "@/lib/apiClient";
import { Calendar, ExcusedLeave } from "@/components/icons";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { reportsExtendedApi } from "../../services/reports-extended.api";
import { useReportData } from "../../hooks/useReportData";
import { useReportConfig } from "../../hooks/useReportConfig";
import styles from "./AttendanceTrackingReportPage.module.scss";

// -----------------------------------------------------------------------------
// API tipleri (backend ile bire bir aynı)
// -----------------------------------------------------------------------------
type ExcuseLabel =
  | "HEALTH"
  | "ANNUAL_LEAVE"
  | "BUSINESS_TRAVEL"
  | "OFFICIAL_DUTY"
  | "FAMILY_EMERGENCY"
  | "OTHER";

interface AttendanceItem {
  educationID: string;
  title: string;
  expected: number;
  present: number;
  absent: number;
  excused: number;
  attendanceRatePercent: number;
  noShowPercent: number;
}

interface AttendanceExcuseBucket {
  label: ExcuseLabel;
  count: number;
}

interface AttendanceMonthlyPoint {
  month: string;
  total: number;
  present: number;
}

interface AttendanceResponse {
  summary: {
    totalRecords: number;
    presentRecords: number;
    absentRecords: number;
    excusedAbsences: number;
    overallAttendanceRatePercent: number;
    noShowPercent: number;
  };
  monthlyTrend: AttendanceMonthlyPoint[];
  byExcuseReason: AttendanceExcuseBucket[];
  byEducation: { label: string; count: number }[];
  items: AttendanceItem[];
  count: number;
}

interface AttendanceParams {
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  departmentID?: string;
  educationID?: string;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
const PAGE_SIZE = 20;
const ENDPOINT = "/api/admin/reports/attendance-tracking";

// TransformResponseInterceptor `{ data: <payload> }` döndürebilir.
const unwrap = <T,>(body: { data?: T } | T): T => {
  if (body && typeof body === "object" && "data" in (body as object)) {
    const inner = (body as { data?: T }).data;
    if (inner !== undefined) return inner;
  }
  return body as T;
};

const fetchAttendance = async (
  params: AttendanceParams,
  signal: AbortSignal,
): Promise<AttendanceResponse> => {
  const res = await apiClient.get(ENDPOINT, { params, signal });
  return unwrap<AttendanceResponse>(res.data);
};

const EXCUSE_TR_LABELS: Record<ExcuseLabel, string> = {
  HEALTH: "SAĞLIK",
  ANNUAL_LEAVE: "YILLIK İZİN",
  BUSINESS_TRAVEL: "İŞ SEYAHATİ",
  OFFICIAL_DUTY: "RESMİ GÖREV",
  FAMILY_EMERGENCY: "AİLE",
  OTHER: "DİĞER",
};

const formatNumber = (v: number) => new Intl.NumberFormat("tr-TR").format(v);
const formatPercent = (v: number) => `${v.toFixed(1)}%`;

// "2026-05" → "2026 May"
const formatMonthLabel = (key: string) => {
  const [y, m] = key.split("-");
  const monthNames = [
    "Oca",
    "Şub",
    "Mar",
    "Nis",
    "May",
    "Haz",
    "Tem",
    "Ağu",
    "Eyl",
    "Eki",
    "Kas",
    "Ara",
  ];
  const idx = Number(m) - 1;
  return `${y} ${monthNames[idx] ?? m}`;
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
const AttendanceTrackingReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const params: AttendanceParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      departmentID: filter.departmentID,
      search: filter.search?.trim() || undefined,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    AttendanceResponse,
    AttendanceParams
  >({
    fetcher: fetchAttendance,
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;
  const monthlyTrend = data?.monthlyTrend ?? [];
  const byExcuseReason = data?.byExcuseReason ?? [];
  const byEducation = data?.byEducation ?? [];

  const monthCategories = monthlyTrend.map((p) => formatMonthLabel(p.month));

  const columns: TableColumn<AttendanceItem>[] = [
    {
      key: "title",
      label: t("reports.attendanceTracking.headerEducation"),
      header: t("reports.attendanceTracking.headerEducation"),
      accessor: (row) => <span className={styles.titleCell}>{row.title}</span>,
    },
    {
      key: "expected",
      label: t("reports.attendanceTracking.headerExpected"),
      header: t("reports.attendanceTracking.headerExpected"),
      accessor: (row) => formatNumber(row.expected),
    },
    {
      key: "present",
      label: t("reports.attendanceTracking.headerPresent"),
      header: t("reports.attendanceTracking.headerPresent"),
      accessor: (row) => (
        <span className={styles.numGood}>{formatNumber(row.present)}</span>
      ),
    },
    {
      key: "absent",
      label: t("reports.attendanceTracking.headerAbsent"),
      header: t("reports.attendanceTracking.headerAbsent"),
      accessor: (row) => (
        <span className={styles.numBad}>{formatNumber(row.absent)}</span>
      ),
    },
    {
      key: "excused",
      label: t("reports.attendanceTracking.headerExcused"),
      header: t("reports.attendanceTracking.headerExcused"),
      accessor: (row) => formatNumber(row.excused),
    },
    {
      key: "attendanceRatePercent",
      label: t("reports.attendanceTracking.headerRate"),
      header: t("reports.attendanceTracking.headerRate"),
      accessor: (row) => (
        <span
          className={
            row.attendanceRatePercent >= 80
              ? styles.numGood
              : row.attendanceRatePercent >= 60
                ? styles.numNeutral
                : styles.numBad
          }
        >
          {formatPercent(row.attendanceRatePercent)}
        </span>
      ),
    },
    {
      key: "noShowPercent",
      label: t("reports.attendanceTracking.headerNoShow"),
      header: t("reports.attendanceTracking.headerNoShow"),
      accessor: (row) => (
        <span
          className={
            row.noShowPercent <= 10
              ? styles.numGood
              : row.noShowPercent <= 25
                ? styles.numNeutral
                : styles.numBad
          }
        >
          {formatPercent(row.noShowPercent)}
        </span>
      ),
    },
  ];

  // R11 yapılandırılabilirliği: kolon ayarları, kayıtlı filtre setleri,
  // zamanlanmış e-posta raporu. Blok düzenini ReportDetailLayout yönetir.
  const cfg = useReportConfig<AttendanceItem>({
    reportKey: "ATTENDANCE_TRACKING",
    reportTitle: t("reports.attendanceTracking.title"),
    columns,
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title={t("reports.attendanceTracking.title")}
      layoutKey={`report:ATTENDANCE_TRACKING`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t("reports.attendanceTracking.categoryLabel")}
      description={t("reports.attendanceTracking.description")}
      exportSlot={
        <>
          {cfg.toolbar}
          <ReportExportButtons
          reportType="ATTENDANCE_TRACKING"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="devamsizlik"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
        </>
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
        />
      }
    >
      {/* -------------------------------------------------------------- KPIs */}
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title={t("reports.attendanceTracking.kpiOverallRate")}
          value={summary ? formatPercent(summary.overallAttendanceRatePercent) : "—"}
          icon={<Calendar width={18} height={18} />}
          accent={
            summary
              ? summary.overallAttendanceRatePercent >= 80
                ? "success"
                : summary.overallAttendanceRatePercent >= 60
                  ? "warning"
                  : "danger"
              : "default"
          }
        />
        <ReportKPICard
          title={t("reports.attendanceTracking.kpiTotalRecords")}
          value={summary ? formatNumber(summary.totalRecords) : "—"}
          icon={<Calendar width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.attendanceTracking.kpiExcused")}
          value={summary ? formatNumber(summary.excusedAbsences) : "—"}
          icon={<ExcusedLeave width={18} height={18} />}
          accent="warning"
        />
        <ReportKPICard
          title={t("reports.attendanceTracking.kpiNoShow")}
          value={summary ? formatPercent(summary.noShowPercent) : "—"}
          icon={<ExcusedLeave width={18} height={18} />}
          accent={
            summary
              ? summary.noShowPercent <= 10
                ? "success"
                : summary.noShowPercent <= 25
                  ? "warning"
                  : "danger"
              : "default"
          }
        />
      </div>

      {/* ------------------------------------------------------ Charts grid */}
      <div className={styles.chartGrid}>
        <ReportChart
          title={t("reports.attendanceTracking.chartMonthlyTitle")}
          type="line"
          isLoading={isLoading}
          isEmpty={monthlyTrend.length === 0}
          height={280}
          series={[
            {
              name: t("reports.attendanceTracking.seriesPresent"),
              data: monthlyTrend.map((p) => p.present),
            },
            {
              name: t("reports.attendanceTracking.seriesTotal"),
              data: monthlyTrend.map((p) => p.total),
            },
          ]}
          options={{
            xaxis: { categories: monthCategories },
            colors: ["#10b981", "#4f46e5"],
            stroke: { curve: "smooth", width: 3 },
            markers: { size: 4 },
          }}
        />

        <ReportChart
          title={t("reports.attendanceTracking.chartExcuseTitle")}
          type="donut"
          isLoading={isLoading}
          isEmpty={byExcuseReason.every((b) => b.count === 0)}
          height={280}
          series={byExcuseReason.map((b) => b.count)}
          options={{
            labels: byExcuseReason.map((b) => EXCUSE_TR_LABELS[b.label]),
            colors: [
              "#ef4444",
              "#f59e0b",
              "#0ea5e9",
              "#8b5cf6",
              "#10b981",
              "#94a3b8",
            ],
            legend: { position: "bottom" },
          }}
        />
      </div>

      <ReportChart
        title={t("reports.attendanceTracking.chartLowestTitle")}
        subtitle={t("reports.attendanceTracking.chartLowestSubtitle")}
        type="bar"
        isLoading={isLoading}
        isEmpty={byEducation.length === 0}
        height={320}
        series={[
          {
            name: t("reports.attendanceTracking.seriesAttendanceRate"),
            data: byEducation.map((b) => b.count),
          },
        ]}
        options={{
          xaxis: { categories: byEducation.map((b) => b.label) },
          colors: ["#ef4444"],
          plotOptions: {
            bar: {
              horizontal: true,
              borderRadius: 4,
              barHeight: "60%",
            },
          },
          yaxis: {
            labels: {
              formatter: (val: number) => `${val.toFixed(0)}%`,
            },
          },
        }}
      />

      {/* --------------------------------------------------------------- Table */}
      {cfg.columnSettings}
      <Table
        columns={cfg.columns}
        data={items}
        isLoading={isLoading}
        contentBasedHeight
        getRowId={(row) => row.educationID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error
            ? t("reports.attendanceTracking.noData")
            : t("reports.attendanceTracking.noResults")
        }
      />
    </ReportDetailLayout>
  );
};

export default AttendanceTrackingReportPage;
